import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import HelpOutlineSharpIcon from '@mui/icons-material/HelpOutlineSharp';
import { Grid, IconButton } from '@mui/material';
import { DragDropContext, Draggable, Droppable, } from '@hello-pangea/dnd';
import SelectInput from '../../atoms/SelectInput';
import Button from '../../atoms/Button';
import { Label1, Text } from '../../atoms/typography';
import { Rule } from './Rule';
import { Tooltip } from '../../atoms/Tooltip';
export const RuleSet = ({ ruleGroup: ruleSet = { operator: 'OR' }, isNested = false, onDelete, index: ruleSetIndex = 0, fields, comparators, onChange, keyRef = '0', draggableProvided, draggableSnapshot, disabledReasonInfo, enableReasons = false, }) => {
    const [hasNestedChecklists, setHasNestedChecklists] = useState(false);
    const [hasLocalChecklists, setHasLocalChecklists] = useState(false);
    const handleChange = (newRuleSet) => {
        onChange?.(newRuleSet, ruleSetIndex);
    };
    /**
     * This useEffect handles the following:
     * 1. It will clear the reason for this ruleSet if the disabledReasonInfo value is populated.
     */
    useEffect(() => {
        if (!!disabledReasonInfo && ruleSet.reason) {
            handleChange({
                ...ruleSet,
                reason: undefined,
            });
        }
    }, [disabledReasonInfo]);
    /**
     * This function determines if a ruleSet or one of its nested ruleSets contain a checklist.
     * @param ruleSet - The ruleSet to check. It will also check nested ruleSets to see if those contain
     * checklists.
     * @returns True if this or a nest ruleSet includes a checklist rule. False otherwise.
     */
    const doesHaveChecklists = (ruleSet) => {
        const keys = Object.keys(ruleSet).map(key => parseInt(key)).filter(val => !isNaN(val));
        let nested = false, local = false;
        for (const key of keys) {
            if (ruleSet[key]?.operator) {
                const { nested: subNested, local: subLocal } = doesHaveChecklists(ruleSet[key]);
                nested = nested || subNested || subLocal;
            }
            else {
                local = local || (ruleSet[key]?.field?.includes('checklist:') ?? false);
            }
        }
        return { nested: nested || local, local };
    };
    useEffect(() => {
        const { nested: hasNestedChecklistsNow, local: hasLocalChecklistsNow, } = doesHaveChecklists(ruleSet);
        if (hasNestedChecklists !== hasNestedChecklistsNow) {
            handleChange({
                ...ruleSet,
                reason: undefined,
            });
        }
        else if (!disabledReasonInfo &&
            (hasNestedChecklistsNow && ruleSet.operator === 'AND') || hasLocalChecklists) {
            handleChange({
                ...ruleSet,
                reason: 'failed_checkpoint_reason',
            });
        }
        setHasNestedChecklists(hasNestedChecklistsNow);
        setHasLocalChecklists(hasLocalChecklistsNow);
    }, [JSON.stringify(ruleSet), disabledReasonInfo]);
    const handleNestedChange = (rule, index) => {
        const newRuleSet = { ...ruleSet };
        newRuleSet[index] = rule;
        handleChange(newRuleSet);
    };
    const getNestedCount = (ruleSet) => {
        return Object.keys(ruleSet).filter(key => !isNaN(parseInt(key))).length;
    };
    const handleDelete = (index) => {
        const newRuleSet = { ...ruleSet };
        const nestedCount = getNestedCount(ruleSet);
        for (let i = index; i < nestedCount - 1; i++) {
            newRuleSet[i] = newRuleSet[i + 1];
        }
        delete newRuleSet[nestedCount - 1];
        handleChange(newRuleSet);
    };
    const handleAddRule = () => {
        const newIndex = getNestedCount(ruleSet);
        const newRuleSet = {
            ...ruleSet,
            [newIndex]: {}
        };
        handleChange(newRuleSet);
    };
    const handleAddRuleGroup = () => {
        const newIndex = getNestedCount(ruleSet);
        const newRuleSet = {
            ...ruleSet,
            [newIndex]: {
                operator: 'OR',
                0: {},
            }
        };
        handleChange(newRuleSet);
    };
    const getOperator = (row, isHidden = false) => {
        const style = {
            opacity: isHidden ? 0 : 1,
        };
        switch (row) {
            case 0:
                return _jsx(Label1, { sx: style, children: "When" });
            case 1:
                return _jsx(SelectInput, { label: 'Operator', shouldHideLabel: true, options: [{ value: 'AND', label: 'and' }, { value: 'OR', label: 'or' }], value: ruleSet.operator ?? 'OR', sx: style, onChange: ((event) => {
                        return handleChange({ ...ruleSet, operator: event.target.value });
                    }), fullWidth: true });
            default:
                return _jsx(Text, { pl: 1, style: style, children: ruleSet.operator?.toLowerCase() ?? ('or' + row) });
        }
    };
    const rows = Object.keys(ruleSet).reduce((acc, key) => {
        const index = parseInt(key);
        if (isNaN(index) || !ruleSet[index])
            return acc;
        return [...acc, (_jsx(Draggable, { draggableId: `draggable-${keyRef}-${index}`, index: index, children: (nestedDraggableProvided, nestedDraggableSnapshot) => {
                    const nestedDisabledReasonInfo = (disabledReasonInfo ??
                        (ruleSet.operator === 'AND' ? 'nested under "And" group.' : undefined) ??
                        (ruleSet.reason ? 'nested under another reason.' : undefined));
                    const sharedProps = {
                        index,
                        fields,
                        comparators,
                        onDelete: () => handleDelete(index),
                        onChange: handleNestedChange,
                        draggableProvided: nestedDraggableProvided,
                        draggableSnapshot: nestedDraggableSnapshot,
                        disabledReasonInfo: nestedDisabledReasonInfo,
                    };
                    return (_jsxs(Grid, { minHeight: '56px', container: true, item: true, direction: 'row', wrap: 'nowrap', alignItems: ruleSet[index].operator ? 'start' : 'center', ref: nestedDraggableProvided.innerRef, ...nestedDraggableProvided.draggableProps, children: [_jsx(Grid, { item: true, width: '100px', pt: !ruleSet[index].operator ? 0 : index === 1 ? 1 : 1.5, children: getOperator(index, nestedDraggableSnapshot.isDragging) }), _jsx(Grid, { container: true, item: true, children: ruleSet[index].operator ? (_jsx(RuleSet, { ruleGroup: ruleSet[index], keyRef: `${keyRef}-${index}`, isNested: true, enableReasons: enableReasons, ...sharedProps })) : (_jsx(Rule, { rule: ruleSet[index], ...sharedProps })) }, `row-${index}`)] }));
                } }, `${keyRef}-${index}`))];
    }, []);
    const deleteGroupButton = useMemo(() => (onDelete && ruleSetIndex !== undefined) ? (_jsx(IconButton, { onClick: () => onDelete?.(), children: _jsx(DeleteIcon, { sx: { color: 'action.active' } }) })) : undefined, [ruleSetIndex, onChange, onDelete]);
    const removeRuleOrGroupFromGroup = (currentRuleGroup, path, index) => {
        const newRuleGroup = { ...currentRuleGroup };
        if (path.length > 0 && currentRuleGroup.operator !== undefined) {
            const { newRuleGroup: newNestedRuleGroup, removedRuleOrGroup, } = removeRuleOrGroupFromGroup(newRuleGroup[parseInt(path[0])], path.slice(1), index);
            newRuleGroup[parseInt(path[0])] = newNestedRuleGroup;
            return {
                newRuleGroup,
                removedRuleOrGroup,
            };
        }
        else {
            const removedRuleOrGroup = currentRuleGroup[index];
            const maxIndex = getNestedCount(currentRuleGroup) - 1;
            for (let i = index; i < maxIndex; i++) {
                newRuleGroup[i] = newRuleGroup[i + 1];
            }
            const deleteIndex = getNestedCount(newRuleGroup) - 1;
            delete newRuleGroup[deleteIndex];
            return { newRuleGroup, removedRuleOrGroup };
        }
    };
    const addRuleOrGroupToGroup = (currentRuleGroup, ruleOrGroupToAdd, path, index) => {
        const newRuleGroup = { ...currentRuleGroup };
        if (path.length > 0) {
            newRuleGroup[parseInt(path[0])] = addRuleOrGroupToGroup(newRuleGroup[parseInt(path[0])], ruleOrGroupToAdd, path.slice(1), index);
        }
        else {
            const startingIndex = getNestedCount(newRuleGroup);
            for (let i = startingIndex; i > index; i--) {
                newRuleGroup[i] = newRuleGroup[i - 1];
            }
            newRuleGroup[index] = ruleOrGroupToAdd;
        }
        return newRuleGroup;
    };
    const handleDefaultReason = (defaultReason) => {
        if (defaultReason && defaultReason !== ruleSet.reason) {
            console.debug('updating with default reason', { defaultReason });
            handleChange({
                ...ruleSet,
                reason: defaultReason,
            });
        }
    };
    const handleDragEnd = ({ source, destination, draggableId }) => {
        if (!destination)
            return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) {
            return;
        }
        const sourceGroupPath = source.droppableId.split('-').slice(2);
        const destinationGroupPath = destination.droppableId.split('-').slice(2);
        const { newRuleGroup, removedRuleOrGroup: ruleOrGroup, } = removeRuleOrGroupFromGroup(ruleSet, sourceGroupPath, source.index);
        const newRuleSet = addRuleOrGroupToGroup(newRuleGroup, ruleOrGroup, destinationGroupPath, destination.index);
        handleChange(newRuleSet);
    };
    let reasonOptions = [];
    let defaultReason = '';
    let reasonTooltip = `When the conditions are met, the reason will be added to the item's history.`;
    if (disabledReasonInfo) {
        reasonTooltip = `${reasonTooltip} This is disabled because it is ${disabledReasonInfo}`;
    }
    else if (hasNestedChecklists) {
        reasonOptions = [{ label: 'Checkpoint Unsellable Reason', value: 'failed_checkpoint_reason' }];
        if (hasLocalChecklists) {
            defaultReason = 'failed_checkpoint_reason';
            reasonTooltip = `${reasonTooltip} Because there are checklists included in this group of
        rules, it requires the use of the unsellable reason of a selected checkpoint.`;
        }
        else if (ruleSet.operator === 'AND') {
            defaultReason = 'failed_checkpoint_reason';
            reasonTooltip = `${reasonTooltip} Because there are checklists included a nested group of
        rules, and this is an AND group, it requires the use of the unsellable reason of a selected
        checkpoint.`;
        }
        else {
            reasonTooltip = `${reasonTooltip} Because there are checklists included in this group of rules,
        only the checkpoint unsellable reason is available to select.`;
        }
    }
    else {
        reasonOptions = [
            'Unsellable Condition',
            'Ineligible Catalog',
            'Unqualified',
            'Failed Authentication',
            'Price Too Low',
        ].map(reason => ({ label: reason, value: reason }));
    }
    handleDefaultReason(defaultReason);
    reasonOptions.unshift({ label: 'None', value: '' });
    const isReasonDisabled = !!disabledReasonInfo || defaultReason === 'failed_checkpoint_reason';
    const getContent = () => (_jsxs(Grid, { bgcolor: 'white', container: true, direction: 'column', rowGap: 1, mx: isNested ? 1 : 0, p: 1, border: isNested ? 1 : 0, borderColor: 'action.disabled', borderRadius: 1, children: [isNested && draggableProvided &&
                _jsxs(Grid, { container: true, item: true, alignItems: 'center', direction: 'row', flex: 1, children: [_jsx(Grid, { item: true, flex: 1, children: _jsxs(Label1, { color: 'text.secondary', children: [ruleSet.operator === 'AND' ? 'All' : 'Any', ' ', "of the following rules match:"] }) }), _jsxs(Grid, { container: true, item: true, flex: 0, direction: 'row', wrap: 'nowrap', children: [deleteGroupButton, _jsx(IconButton, { ...draggableProvided?.dragHandleProps, children: _jsx(DragIndicatorIcon, { sx: { color: 'action.active' } }) })] })] }), (_jsxs(_Fragment, { children: [_jsx(Droppable, { droppableId: `droppable-${keyRef}`, type: `droppable-${keyRef}`, children: (droppableProvided) => {
                            return (_jsxs(Grid, { item: true, container: true, rowGap: 0.5, marginBottom: 0.5, ref: droppableProvided.innerRef, ...droppableProvided.droppableProps, children: [rows, droppableProvided.placeholder] }));
                        } }), _jsxs(Grid, { item: true, container: true, wrap: 'nowrap', justifyContent: 'space-between', children: [_jsxs(Grid, { item: true, container: true, columnGap: 0.5, children: [_jsx(Grid, { item: true, children: _jsx(Button, { label: 'Add a rule', leftIcon: _jsx(AddIcon, {}), onClick: handleAddRule }) }), _jsx(Grid, { item: true, children: _jsx(Button, { variant: 'text', label: 'Add a rule group', leftIcon: _jsx(AddIcon, {}), onClick: handleAddRuleGroup }) })] }), enableReasons &&
                                _jsxs(Grid, { item: true, container: true, minWidth: '230px', xs: 'auto', wrap: 'nowrap', alignItems: 'center', justifyContent: 'center', children: [_jsx(SelectInput, { label: 'Reason', size: 'small', sx: {
                                                backgroundColor: isReasonDisabled ? '#DDDDDD' : undefined,
                                            }, options: reasonOptions, disabled: isReasonDisabled, value: !disabledReasonInfo ? ruleSet?.reason ?? defaultReason : '', onChange: ((event) => {
                                                return handleChange({ ...ruleSet, reason: event.target.value ? event.target.value : undefined });
                                            }), fullWidth: true }), _jsx(Tooltip, { text: reasonTooltip, placement: 'top', children: _jsx(HelpOutlineSharpIcon, { sx: { color: 'text.secondary', marginLeft: 1 } }) })] })] })] }))] }));
    return isNested ? getContent() : (_jsx(DragDropContext, { onDragEnd: handleDragEnd, children: getContent() }));
};
//# sourceMappingURL=index.js.map